import { useEffect, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MultiSelect } from '../multi-select';

import {
  assistancePrograms,
  calculateIncomeLimit,
  MAX_HOUSEHOLD_OCCUPANCY_LIMIT,
  mnDefaultCounties,
  njDefaultCounties,
} from './tabs-data.utils';
import { StyledSelect } from './tabs-data.style';

type Option = { text: string; value: string };
export type TabsDataType = {
  county?: string;
  houseOccupancy?: string;
  houseIncome?: string;
  lmiProgram?: string | string[];
};

export interface TabsDataProps {
  state: string | null;
  onChange: (data: TabsDataType) => void;
  counties?: string[];
  incomeLimits?: Record<string, string[]>;
  programOptions?: Record<string, string | string[]> | null;
  existingProofDocs?:
    | ({
        proofDocUrls: (string | null)[];
        proofDocName: string | null;
        proofDocStatus: string | null;
        proofDocUploadedAt: string | null;
        uaLmiSourceTypeId: number | null;
        utilityAccountId: number | null;
        utilityName: string | null;
        utilityAccountNumber: string | null;
        utilityServiceAddress: string | null;
      } | null)[]
    | null;
}

export const TabsData = (props: TabsDataProps): JSX.Element => {
  const {
    state,
    onChange,
    existingProofDocs,
    counties,
    incomeLimits,
    programOptions,
  } = props;
  const [county, setCounty] = useState<string>('');
  const [houseOccupancy, setHouseOccupancy] = useState<string>('');
  const [houseIncome, setHouseIncome] = useState<string>('');
  const [lmiProgram, setLmiProgram] = useState<string>('');
  // VA uses multiselect — tracked separately as an array
  const [lmiPrograms, setLmiPrograms] = useState<string[]>([]);
  // VA multiselect tracks selected values (resolved to labels on emit)
  const [lmiProgramValues, setLmiProgramValues] = useState<string[]>([]);
  const flag = useFlags();
  const [lmiProofDocTypeOptions] = useState(
    flag.ffUtilityLmiProofDocsLabelOptions
  );

  const houseOccupancyOptions = useMemo(() => {
    const options: Array<{
      disabled: boolean;
      selected: boolean;
      text: string;
      value: string;
    }> = [];
    for (let i = 1; i <= MAX_HOUSEHOLD_OCCUPANCY_LIMIT; i++) {
      options[i] = {
        disabled: false,
        selected: false,
        text: `${i > 1 ? `${i} Persons` : `${i} Person`}`,
        value: i.toString(),
      };
    }
    return options;
  }, []);

  const lmiProgramOptions = useMemo(() => {
    // For MN with valid proof docs, always restrict options to what the user has uploaded
    if (state === 'MN' && existingProofDocs) {
      const validDocNames = new Set(
        existingProofDocs
          .filter(doc => doc?.proofDocName)
          .map(doc => doc?.proofDocName as string)
      );
      const sourceOptions = programOptions ?? lmiProofDocTypeOptions;
      const filteredKeys = Object.keys(sourceOptions).filter(key =>
        validDocNames.has(key)
      );
      const isSingle = filteredKeys.length === 1;
      return filteredKeys.map(programKey => ({
        disabled: false,
        selected: isSingle,
        text:
          typeof sourceOptions[programKey] === 'string'
            ? (sourceOptions[programKey] as string)
            : programKey,
        value: programKey,
      }));
    }

    // Use fetched program options if available
    if (programOptions) {
      const keys = Object.keys(programOptions);
      const values = Object.values(programOptions);

      // MA format: single key whose value is an array of program names →
      // expand each array item as its own selectable option (value === text).
      if (keys.length === 1 && Array.isArray(values[0])) {
        return (values[0] as string[]).map(program => ({
          disabled: false,
          selected: false,
          text: program,
          value: program,
        }));
      }

      return keys.map(programKey => ({
        disabled: false,
        selected: false,
        text:
          typeof programOptions[programKey] === 'string'
            ? (programOptions[programKey] as string)
            : programKey,
        value: programKey,
      }));
    }

    switch (state) {
      case 'NJ':
        return Object.keys(assistancePrograms).map(programKey => ({
          disabled: false,
          selected: false,
          text: assistancePrograms[programKey],
          value: programKey,
        }));
      default:
        return Object.keys(lmiProofDocTypeOptions).map(programKey => ({
          disabled: false,
          selected: false,
          text: lmiProofDocTypeOptions[programKey],
          value: programKey,
        }));
    }
  }, [state, existingProofDocs, lmiProofDocTypeOptions, programOptions]);

  useEffect(() => {
    const formatNumberWithCommas = (value: number): string =>
      value.toLocaleString('en-US');

    const handleHouseIncome = () => {
      // Use fetched income limits if available
      if (incomeLimits && county && houseOccupancy) {
        const income = calculateIncomeLimit(
          county,
          houseOccupancy,
          incomeLimits
        );
        setHouseIncome(income !== null ? formatNumberWithCommas(income) : '');
      } else {
        setHouseIncome('');
      }
    };

    if (state && county && houseOccupancy) {
      handleHouseIncome();
    }
  }, [state, county, houseOccupancy, incomeLimits]);

  const getTabsDataFields = state => {
    switch (state) {
      case 'NJ':
        return ['County', 'House Occupancy', 'LMI Program'];
      case 'MN':
        return ['County', 'House Occupancy', 'LMI Program'];
      case 'NY':
        return ['County', 'House Occupancy'];
      case 'MA':
        return ['County', 'House Occupancy', 'LMI Program'];
      case 'VA':
        return ['County', 'House Occupancy', 'LMI Program'];
      case 'MD':
        return ['County', 'House Occupancy'];
      default:
        return [];
    }
  };

  const createOption = (county: string) => {
    const option: Option = {
      text: county,
      value: county,
    };
    return option;
  };

  const handleTextInput = (field: string, value: string) => {
    switch (field.toLowerCase()) {
      case 'county':
        setCounty(value);
        break;
      case 'household occupancy':
        setHouseOccupancy(value);
        break;
      case 'lmi program':
        setLmiProgram(
          lmiProgramOptions?.find(option => option.value === value)?.text
        );
        break;
      default:
        break;
    }
  };

  const getDefaultCounties = (state: string) => {
    // Use fetched counties if available
    if (counties && counties.length > 0) {
      return counties.map(createOption);
    }

    // Fall back to static data
    switch (state) {
      case 'NJ':
        return njDefaultCounties.map(createOption);
      case 'MN':
        return mnDefaultCounties.map(createOption);
      default:
        return [];
    }
  };

  // Auto-select county when only one option is available
  useEffect(() => {
    if (!state || county) return;
    const countyOptions = getDefaultCounties(state);
    if (countyOptions.length === 1) {
      setCounty(countyOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, counties]);

  // Auto-select when there is exactly one LMI program option (MN single proof doc case)
  useEffect(() => {
    if (state === 'MN' && lmiProgramOptions?.length === 1) {
      const singleOption = lmiProgramOptions[0];
      setLmiProgram(singleOption.text);
    }
  }, [state, lmiProgramOptions]);

  useEffect(() => {
    const fieldValues: TabsDataType = {
      county,
      houseOccupancy,
      houseIncome,
      // VA emits the multiselect array; all other states emit the single string
      lmiProgram: state === 'VA' ? lmiPrograms : lmiProgram,
    };

    // Always emit current state so parent interpolations stay up-to-date.
    // isFormReady in the parent handles completeness validation.
    onChange(fieldValues);
  }, [
    state,
    county,
    houseOccupancy,
    houseIncome,
    lmiProgram,
    lmiPrograms,
    onChange,
  ]);

  const renderSelectField = (
    field: string,
    options: Option[],
    isRequired: boolean,
    isDisabled?: boolean,
    displayLabel?: string,
    value?: string
  ) => (
    <StyledSelect
      label={displayLabel ?? field}
      name={field}
      options={options}
      required={isRequired}
      withEmptyPlaceholder
      disabled={isDisabled}
      value={value}
      onChange={value => handleTextInput(field, value)}
    />
  );

  const renderField = (field: string) => {
    if (!state) return null;
    if (!lmiProgramOptions) return null;
    const isRequired = state === 'MN';
    switch (field) {
      case 'County': {
        const countyOptions = getDefaultCounties(state);
        const isSingleCounty = countyOptions.length === 1;
        return renderSelectField(
          field,
          countyOptions,
          true,
          isSingleCounty,
          undefined,
          county
        );
      }
      case 'House Occupancy':
        return renderSelectField(
          'Household occupancy',
          houseOccupancyOptions,
          true
        );
      case 'LMI Program': {
        const isSingleOption =
          state === 'MN' && lmiProgramOptions?.length === 1;

        if (state === 'VA') {
          return (
            <MultiSelect
              name="lmi-program"
              label="Select qualifying programs (if applicable)"
              options={lmiProgramOptions.map(o => ({
                value: o.value,
                label: o.text,
              }))}
              value={lmiProgramValues}
              onChange={values => {
                setLmiProgramValues(values);
                const labels = values
                  .map(
                    v => lmiProgramOptions.find(o => o.value === v)?.text ?? v
                  )
                  .filter(Boolean);
                setLmiPrograms(labels);
              }}
            />
          );
        }

        return renderSelectField(
          'LMI program',
          lmiProgramOptions,
          isRequired,
          isSingleOption,
          'Select qualifying program (if applicable)'
        );
      }
      default:
        return null;
    }
  };

  return (
    <div>
      {state &&
        getTabsDataFields(state).map(field => (
          <div key={field}>{renderField(field)}</div>
        ))}
    </div>
  );
};
